import { toast } from "sonner";
import { MapPin, Clock, CalendarClock, Check, X, HelpCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useFetch, usePositions } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";
import { dateParts, fmtShort, fmtRange, fmtTime } from "@/lib/format";

const CHOICES = [
  ["available", Check, "bg-emerald-600 text-white"],
  ["maybe", HelpCircle, "bg-[#C5A059] text-[#0A0A0C]"],
  ["unavailable", X, "bg-red-500/85 text-white"],
];

export const useAvailability = (enabled = true) => {
  const { t } = useLang();
  const { data, setData } = useFetch(enabled ? "/me/available" : null);
  const onChange = async (e, status, slots) => {
    const prev = data;
    setData(data.map((x) => (x.id === e.id ? { ...x, my_availability: status, my_slots: slots } : x)));
    try {
      await api.put("/me/availability", { event_id: e.id, status, slots });
      toast.success(t("p.savedAvail"), { duration: 1200 });
    } catch (err) {
      setData(prev);
      toast.error(errorText(t, err));
    }
  };
  return { data, onChange };
};

export const EventCard = ({ e, onChange, i = 0 }) => {
  const { t, lang } = useLang();
  const { label } = usePositions();
  const d = dateParts(e.date, lang);
  const locked = e.deadline_passed || e.assigned;
  const save = (status, slots) => onChange(e, status, slots);
  const toggleInterest = (sid) => {
    const next = e.my_slots.includes(sid) ? e.my_slots.filter((x) => x !== sid) : [...e.my_slots, sid];
    save(e.my_availability && e.my_availability !== "unavailable" ? e.my_availability : "available", next);
  };

  return (
    <article className="card-lux rise overflow-hidden" style={{ animationDelay: `${i * 60}ms` }} data-testid={`available-event-${e.id}`}>
      <div className="flex gap-4 p-5 pb-4">
        <div className="w-14 h-16 shrink-0 rounded-xl border border-[#C5A059]/40 bg-[#C5A059]/[0.06] flex flex-col items-center justify-center">
          <span className="text-[9px] uppercase tracking-[0.2em] text-[#D9B872]">{d.month}</span>
          <span className="mono text-2xl leading-none mt-0.5">{d.day}</span>
        </div>
        <div className="min-w-0 flex-1 pr-6">
          <h3 className="font-semibold text-base leading-snug">{e.name}</h3>
          <div className="mt-2 space-y-1 text-xs text-neutral-600">
            <div className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#C5A059]" />{e.location}</div>
            <div className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-[#C5A059]" /><span className="mono">{fmtRange(e.start_time, e.end_time, t)}</span>{e.report_time && <span className="text-neutral-400">· {t("c.reporting")} {fmtTime(e.report_time, t)}</span>}</div>
            {e.availability_deadline && (
              <div className={`flex items-center gap-1.5 ${e.deadline_passed ? "text-red-600" : ""}`}><CalendarClock className="w-3.5 h-3.5 text-[#C5A059]" />{t("c.deadline")} : <span className="capitalize">{fmtShort(e.availability_deadline, lang)}</span></div>
            )}
          </div>
        </div>
      </div>
      <div className="px-5 pb-5">
        <div className="eyebrow mb-2">{t("p.interested")}</div>
        <div className="flex flex-wrap gap-2 mb-5">
          {e.positions.map((p) => {
            const on = e.my_slots.includes(p.id);
            return (
              <button key={p.id} disabled={locked || e.my_availability === "unavailable"} onClick={() => toggleInterest(p.id)} data-testid={`interest-${e.id}-${p.id}`} aria-pressed={on}
                className={`press text-left border rounded-xl px-3 py-2 text-xs disabled:opacity-50 ${on ? "border-[#C5A059] bg-[#C5A059]/10" : "border-black/10 hover:border-black/30"}`}>
                <div className="font-medium">{label(p.position_key)}</div>
                <div className="mono text-[10px] text-neutral-500 mt-0.5">{fmtRange(p.start_time || e.start_time, p.end_time || e.end_time, t)} · {p.open} {t("p.open")}</div>
              </button>
            );
          })}
        </div>
        {e.assigned ? (
          <div className="text-sm rounded-full text-emerald-700 border border-emerald-200 bg-emerald-50 px-4 py-3 text-center" data-testid={`assigned-note-${e.id}`}>{t("p.alreadyAssigned")}</div>
        ) : (
          <div className="grid grid-cols-3 gap-1 rounded-full bg-black/40 border border-white/[0.06] p-1" data-testid={`availability-choices-${e.id}`}>
            {CHOICES.map(([k, Icon, active]) => (
              <button key={k} disabled={e.deadline_passed} onClick={() => save(k, k === "unavailable" ? [] : e.my_slots)} data-testid={`availability-${k}-${e.id}`} aria-pressed={e.my_availability === k}
                className={`press h-10 rounded-full flex items-center justify-center gap-1.5 text-xs sm:text-sm font-medium disabled:opacity-40 ${e.my_availability === k ? `${active} font-semibold` : "text-neutral-600 hover:text-black"}`}>
                <Icon className="w-4 h-4" />{t(`av.${k}`)}
              </button>
            ))}
          </div>
        )}
        {e.deadline_passed && !e.assigned && <p className="text-xs text-red-600 mt-2">{t("p.closed")}</p>}
      </div>
    </article>
  );
};

export const AvailabilityDialog = ({ event, onClose, onChange }) => (
  <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="p-0 bg-transparent border-0 shadow-none max-w-md w-[calc(100%-2rem)]" data-testid="availability-dialog">
      <DialogTitle className="sr-only">{event?.name}</DialogTitle>
      {event && <EventCard e={event} onChange={onChange} />}
    </DialogContent>
  </Dialog>
);
